import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { getCurrentUserId } from 'common-src/features/auth';
import { getDashboardV2Appointments } from 'common-src/models/DashboardV2Appointment';
import { getDashboardV2Tasks } from 'common-src/models/DashboardV2Task';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import {
  getCareOrchestrationIsLoading,
  setCareOrchestrationError,
  setCareOrchestrationLoading,
} from 'src/features/careOrchestration';
import {
  getCareOrchestrationState,
  saveCareOrchestrationState,
  saveCareOrchestrationView,
} from 'src/features/tabsState';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';

import styles from './CareOrchestration.module.scss';
import CareOrchestrationHeader from './components/CareOrchestrationHeader';
import ReadinessView from './components/ReadinessView';
import TasksView from './components/TasksView';
import {
  CARE_ORCHESTRATION_BASE_PATH,
  DEFAULT_OWNER_STATS,
  DEFAULT_STATS,
  PRIORITY_SORT_ORDER,
  TAB_KEYS,
  TASK_CALLBACK_STATUS,
  TASK_COMPLETE_REFRESH_ATTEMPTS,
  TASK_COMPLETE_REFRESH_INTERVAL_MS,
  TASK_COMPLETE_STABLE_REFRESHES,
  TASK_DISPLAY_STATUS,
  TASK_PRIORITY,
} from './constants';
import {
  getAppointmentReadinessStatus,
  getCareOrchestrationPath,
  getTaskDisplayStatus,
  getTaskPatientGroupKey,
  getTaskPatientId,
  groupAppointmentsByDate,
  isAssessmentTask,
  isMessageTask,
  isTaskBlockedStatus,
  isTaskEscalatedStatus,
  matchesTaskStatusFilter,
  parseCareOrchestrationRoute,
  shouldHideTaskByCallbackStatus,
  sortAppointmentsByDate,
} from './helpers';
import {
  assignTaskToMe,
  blockTask,
  deescalateTask,
  escalateTask,
  fetchDashboardSupplementalData,
  fetchTaskQueueData,
  markTaskComplete,
  reassignTask,
  unassignTask,
  unblockTask,
} from './services/careOrchestrationApi';
import { syncCareOrchestrationData } from './services/syncCareOrchestrationData';

const getRelatedVisibleTaskSignature = (task, taskRows = []) => {
  const appointmentId = `${task?.externalObjectId || ''}`.trim();
  if (!appointmentId) return null;

  return taskRows
    .filter((candidateTask) => {
      const candidateAppointmentId = `${candidateTask?.externalObjectId || ''}`.trim();
      if (!candidateAppointmentId || candidateAppointmentId !== appointmentId) return false;
      return (candidateTask?.isVisible ?? true) === true;
    })
    .map((candidateTask) => `${candidateTask?.id || ''}`.trim())
    .filter(Boolean)
    .sort()
    .join('|');
};

const isTaskBlockStateAlreadyCurrentError = (errorMessage = '') => {
  const normalizedMessage = `${errorMessage}`.trim().toLowerCase();

  return (
    normalizedMessage.includes('task is already blocked') ||
    normalizedMessage.includes('task is already unblocked')
  );
};

const isTaskEscalationStateAlreadyCurrentError = (errorMessage = '') => {
  const normalizedMessage = `${errorMessage}`.trim().toLowerCase();

  return (
    normalizedMessage.includes('task is already escalated') ||
    normalizedMessage.includes('task is already deescalated')
  );
};

const isCompletedTaskSettled = (taskId, nextTaskRows = []) => {
  const completedTask = nextTaskRows.find((task) => task?.id === taskId);
  if (!completedTask) return true;

  const normalizedCallbackStatus = `${completedTask?.callbackStatus || ''}`.trim().toLowerCase();
  return (
    getTaskDisplayStatus(completedTask) === TASK_DISPLAY_STATUS.Done ||
    normalizedCallbackStatus === TASK_CALLBACK_STATUS.Success
  );
};

export default function MindspanCareOrchestration() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { showAlert, AlertType } = useAlert();
  const persistedState = useCustomSelector(getCareOrchestrationState);

  const [ownerFilter, setOwnerFilter] = useState(persistedState?.ownerFilter || 'all');
  const [activeTeam, setActiveTeam] = useState(persistedState?.activeTeam || 'all');
  const [statusFilter, setStatusFilter] = useState(persistedState?.statusFilter || 'open');
  const [patientSearch, setPatientSearch] = useState(persistedState?.patientSearch || null);
  const [sortKey, setSortKey] = useState(persistedState?.sortKey || 'due');
  const [sortDirection, setSortDirection] = useState(persistedState?.sortDirection || 'asc');
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [taskFromReadiness, setTaskFromReadiness] = useState(null);
  const [expandedPatients, setExpandedPatients] = useState(persistedState?.expandedPatients || {});
  const [assigningTaskId, setAssigningTaskId] = useState(null);
  const [unassigningTaskId, setUnassigningTaskId] = useState(null);
  const [markingTaskId, setMarkingTaskId] = useState(null);
  const [reassigningTaskId, setReassigningTaskId] = useState(null);
  const [blockingTaskId, setBlockingTaskId] = useState(null);
  const [unblockingTaskId, setUnblockingTaskId] = useState(null);
  const [escalatingTaskId, setEscalatingTaskId] = useState(null);
  const [deescalatingTaskId, setDeescalatingTaskId] = useState(null);
  const [sessionCompletedTaskIds, setSessionCompletedTaskIds] = useState({});
  const appliedTaskRouteKeyRef = useRef(null);

  const tasks = useCustomSelector(getDashboardV2Tasks);
  const currentUser = useCustomSelector((state) =>
    singleModelSelector(state, 'Client', getCurrentUserId(state)),
  );
  const isCareOrchestrationLoading = useCustomSelector(getCareOrchestrationIsLoading);
  const appointments = useCustomSelector(getDashboardV2Appointments);
  const isReadinessEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'careOrchestrationReadiness'),
  );
  const isTasksEnabled = useCustomSelector((state) =>
    isFeatureEnabled(state, 'careOrchestrationTasks'),
  );
  const currentUserName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
  const currentUserNameLower = currentUserName.toLowerCase();

  const availableTabs = useMemo(
    () => [
      { key: TAB_KEYS.TASKS, enabled: isTasksEnabled },
      { key: TAB_KEYS.READINESS, enabled: isReadinessEnabled },
    ],
    [isReadinessEnabled, isTasksEnabled],
  );

  const firstEnabledTab = useMemo(
    () => availableTabs.find((tab) => tab.enabled)?.key ?? null,
    [availableTabs],
  );

  const hasEnabledTabs = !!firstEnabledTab;
  const requestedRoute = useMemo(
    () => parseCareOrchestrationRoute(location.pathname),
    [location.pathname],
  );
  const requestedView = requestedRoute.view;
  const view = useMemo(() => {
    const enabledViews = availableTabs.filter((tab) => tab.enabled).map((tab) => tab.key);

    if (!enabledViews.length) return null;
    if (requestedView && enabledViews.includes(requestedView)) return requestedView;
    return firstEnabledTab;
  }, [availableTabs, firstEnabledTab, requestedView]);

  const availableTasks = useMemo(
    () =>
      tasks.filter(
        (task) => sessionCompletedTaskIds[task.id] || !shouldHideTaskByCallbackStatus(task),
      ),
    [sessionCompletedTaskIds, tasks],
  );

  const tasksById = useMemo(
    () => availableTasks.reduce((acc, task) => ({ ...acc, [task.id]: task }), {}),
    [availableTasks],
  );

  const appointmentsById = useMemo(
    () =>
      appointments.reduce((acc, appointment) => ({ ...acc, [appointment.id]: appointment }), {}),
    [appointments],
  );
  const sortedAppts = useMemo(() => sortAppointmentsByDate(appointments), [appointments]);
  const apptsByDate = useMemo(() => groupAppointmentsByDate(sortedAppts), [sortedAppts]);
  const appointmentTasksById = useMemo(() => {
    const tasksByAppointmentId = {};

    tasks.forEach((task) => {
      const appointmentId = `${task?.externalObjectId || ''}`.trim();
      if (!appointmentId) return;

      if (!tasksByAppointmentId[appointmentId]) {
        tasksByAppointmentId[appointmentId] = [];
      }

      tasksByAppointmentId[appointmentId].push(task);
    });

    Object.keys(tasksByAppointmentId).forEach((appointmentId) => {
      tasksByAppointmentId[appointmentId].sort((a, b) => {
        const aPriority = a?.priority ?? '';
        const bPriority = b?.priority ?? '';
        const priorityDiff =
          (PRIORITY_SORT_ORDER[aPriority] ?? Number.POSITIVE_INFINITY) -
          (PRIORITY_SORT_ORDER[bPriority] ?? Number.POSITIVE_INFINITY);
        if (priorityDiff !== 0) return priorityDiff;

        const aDue = a?.due ? new Date(a.due).getTime() : Number.POSITIVE_INFINITY;
        const bDue = b?.due ? new Date(b.due).getTime() : Number.POSITIVE_INFINITY;
        return aDue - bDue;
      });
    });

    return tasksByAppointmentId;
  }, [tasks]);
  const appointmentTaskLookupById = useMemo(
    () =>
      Object.values(appointmentTasksById)
        .flat()
        .reduce((acc, task) => ({ ...acc, [task.id]: task }), {}),
    [appointmentTasksById],
  );

  const refreshCareOrchestrationData = async () => {
    const { taskQueueRows, taskQueueMeta } = await fetchTaskQueueData();
    const { appointmentsBody, statsBody } = await fetchDashboardSupplementalData();
    syncCareOrchestrationData({
      dispatch,
      taskQueueRows,
      taskQueueMeta,
      appointmentsBody,
      statsBody,
    });
  };

  const wait = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const refreshUntilTaskCompletionSettles = async (task) => {
    const shouldWaitForVisibilityStability = !!`${task?.externalObjectId || ''}`.trim();
    let previousVisibleTaskSignature = null;
    let stableRefreshCount = 0;

    const attemptRefresh = async (attempt = 0) => {
      if (attempt >= TASK_COMPLETE_REFRESH_ATTEMPTS) return;

      if (attempt > 0) {
        await wait(TASK_COMPLETE_REFRESH_INTERVAL_MS);
      }

      const { taskQueueRows, taskQueueMeta } = await fetchTaskQueueData();
      const { appointmentsBody, statsBody } = await fetchDashboardSupplementalData();

      syncCareOrchestrationData({
        dispatch,
        taskQueueRows,
        taskQueueMeta,
        appointmentsBody,
        statsBody,
      });

      if (!isCompletedTaskSettled(task?.id, taskQueueRows)) {
        return attemptRefresh(attempt + 1);
      }

      if (!shouldWaitForVisibilityStability) {
        return;
      }

      const nextVisibleTaskSignature = getRelatedVisibleTaskSignature(task, taskQueueRows);

      if (nextVisibleTaskSignature === previousVisibleTaskSignature) {
        stableRefreshCount += 1;
      } else {
        previousVisibleTaskSignature = nextVisibleTaskSignature;
        stableRefreshCount = 0;
      }

      if (stableRefreshCount >= TASK_COMPLETE_STABLE_REFRESHES) {
        return;
      }

      return attemptRefresh(attempt + 1);
    };

    await attemptRefresh();
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      dispatch(setCareOrchestrationLoading(true));
      dispatch(setCareOrchestrationError(null));

      try {
        const { taskQueueRows, taskQueueMeta } = await fetchTaskQueueData();
        const { appointmentsBody, statsBody } = await fetchDashboardSupplementalData();

        if (cancelled) return;

        syncCareOrchestrationData({
          dispatch,
          taskQueueRows,
          taskQueueMeta,
          appointmentsBody,
          statsBody,
        });
      } catch (error) {
        if (cancelled) return;

        const errorMessage = error?.message || 'Failed to load care orchestration data';
        dispatch(setCareOrchestrationError(errorMessage));
        showAlert(AlertType.Error, '', errorMessage);
      } finally {
        if (!cancelled) {
          dispatch(setCareOrchestrationLoading(false));
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const handleAssignToMe = async (taskId) => {
    if (!taskId || assigningTaskId) return;

    setAssigningTaskId(taskId);

    try {
      await assignTaskToMe(taskId);
      await refreshCareOrchestrationData();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to assign task';
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setAssigningTaskId(null);
    }
  };

  const handleMarkComplete = async (taskId) => {
    if (!taskId || markingTaskId) return;

    setMarkingTaskId(taskId);

    let task = null;

    try {
      task = tasksById[taskId];
      await markTaskComplete(taskId);
      setSessionCompletedTaskIds((prev) => ({ ...prev, [taskId]: true }));
      showAlert(AlertType.Success, '', 'Task successfully marked as completed.');
    } catch (error) {
      const errorMessage = error?.message || 'Failed to mark task complete';
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    }

    try {
      if (task?.workflowInstanceId) {
        await refreshUntilTaskCompletionSettles(task);
      } else {
        await refreshCareOrchestrationData();
      }
      return true;
    } catch (error) {
      return false;
    } finally {
      setMarkingTaskId(null);
    }
  };

  const handleUnassignTask = async (taskId) => {
    if (!taskId || unassigningTaskId) return false;

    setUnassigningTaskId(taskId);

    try {
      await unassignTask(taskId);
      await refreshCareOrchestrationData();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to unassign task';
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setUnassigningTaskId(null);
    }
  };

  const handleReassignTask = async (taskId, assigneeId) => {
    const normalizedAssigneeId = Number(assigneeId);
    if (!taskId || !Number.isInteger(normalizedAssigneeId) || reassigningTaskId) return false;

    setReassigningTaskId(taskId);

    try {
      await reassignTask(taskId, normalizedAssigneeId);
      await refreshCareOrchestrationData();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to reassign task';
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setReassigningTaskId(null);
    }
  };

  const handleBlockTask = async (taskId, blockedReason) => {
    if (!taskId || blockedReason == null || blockingTaskId) return false;

    setBlockingTaskId(taskId);

    try {
      await blockTask(taskId, blockedReason);
      showAlert(AlertType.Success, '', 'Task successfully blocked.');
      await refreshCareOrchestrationData();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to block task';
      if (isTaskBlockStateAlreadyCurrentError(errorMessage)) {
        await refreshCareOrchestrationData().catch(() => null);
      }
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setBlockingTaskId(null);
    }
  };

  const handleUnblockTask = async (taskId) => {
    if (!taskId || unblockingTaskId) return false;

    setUnblockingTaskId(taskId);

    try {
      await unblockTask(taskId);
      showAlert(AlertType.Success, '', 'Task successfully unblocked.');
      await refreshCareOrchestrationData();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to unblock task';
      if (isTaskBlockStateAlreadyCurrentError(errorMessage)) {
        await refreshCareOrchestrationData().catch(() => null);
      }
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setUnblockingTaskId(null);
    }
  };

  const handleEscalateTask = async (taskId, escalatedTo, escalatedReason) => {
    const normalizedEscalatedTo = Number(escalatedTo);
    const normalizedEscalatedReason = `${escalatedReason || ''}`.trim();

    if (!taskId || !Number.isInteger(normalizedEscalatedTo) || escalatingTaskId) {
      return false;
    }

    setEscalatingTaskId(taskId);

    try {
      await escalateTask(taskId, normalizedEscalatedReason, normalizedEscalatedTo);
      showAlert(AlertType.Success, '', 'Task successfully escalated.');
      await refreshCareOrchestrationData();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to escalate task';
      if (isTaskEscalationStateAlreadyCurrentError(errorMessage)) {
        await refreshCareOrchestrationData().catch(() => null);
      }
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setEscalatingTaskId(null);
    }
  };

  const handleDeescalateTask = async (taskId) => {
    if (!taskId || deescalatingTaskId) return false;

    setDeescalatingTaskId(taskId);

    try {
      await deescalateTask(taskId);
      showAlert(AlertType.Success, '', 'Task successfully de-escalated.');
      await refreshCareOrchestrationData();
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to de-escalate task';
      if (isTaskEscalationStateAlreadyCurrentError(errorMessage)) {
        await refreshCareOrchestrationData().catch(() => null);
      }
      showAlert(AlertType.Error, '', errorMessage);
      return false;
    } finally {
      setDeescalatingTaskId(null);
    }
  };

  useEffect(() => {
    if (selectedTask?.id && !tasksById[selectedTask.id]) {
      setSelectedTask(null);
      dispatch(saveCareOrchestrationState({ selectedTaskId: null }));
    }
    if (taskFromReadiness?.id && !appointmentTaskLookupById[taskFromReadiness.id]) {
      setTaskFromReadiness(null);
      dispatch(saveCareOrchestrationState({ taskFromReadinessId: null }));
    }
  }, [appointmentTaskLookupById, dispatch, selectedTask, taskFromReadiness, tasksById]);

  useEffect(() => {
    if (!selectedTask?.id) return;
    const nextSelectedTask = tasksById[selectedTask.id];
    if (!nextSelectedTask) return;
    if (nextSelectedTask !== selectedTask) {
      setSelectedTask(nextSelectedTask);
    }
  }, [selectedTask, tasksById]);

  useEffect(() => {
    if (!taskFromReadiness?.id) return;
    const nextTaskFromReadiness = appointmentTaskLookupById[taskFromReadiness.id];
    if (!nextTaskFromReadiness) return;
    if (nextTaskFromReadiness !== taskFromReadiness) {
      setTaskFromReadiness(nextTaskFromReadiness);
    }
  }, [appointmentTaskLookupById, taskFromReadiness]);

  useEffect(() => {
    if (selectedAppt?.id && !appointmentsById[selectedAppt.id]) {
      setSelectedAppt(null);
      dispatch(saveCareOrchestrationState({ selectedApptId: null }));
    }
  }, [appointmentsById, dispatch, selectedAppt]);

  useEffect(() => {
    if (!selectedAppt?.id) return;
    const nextSelectedAppt = appointmentsById[selectedAppt.id];
    if (!nextSelectedAppt) return;
    if (nextSelectedAppt !== selectedAppt) {
      setSelectedAppt(nextSelectedAppt);
    }
  }, [selectedAppt, appointmentsById]);

  useEffect(() => {
    if (selectedTask || !persistedState?.selectedTaskId) return;
    const nextSelectedTask = tasksById[persistedState.selectedTaskId];
    if (nextSelectedTask) {
      setSelectedTask(nextSelectedTask);
    }
  }, [persistedState?.selectedTaskId, selectedTask, tasksById]);

  useEffect(() => {
    if (taskFromReadiness || !persistedState?.taskFromReadinessId) return;
    const nextTaskFromReadiness = appointmentTaskLookupById[persistedState.taskFromReadinessId];
    if (nextTaskFromReadiness) {
      setTaskFromReadiness(nextTaskFromReadiness);
    }
  }, [appointmentTaskLookupById, persistedState?.taskFromReadinessId, taskFromReadiness]);

  useEffect(() => {
    if (selectedAppt || !persistedState?.selectedApptId) return;
    const nextSelectedAppt = appointmentsById[persistedState.selectedApptId];
    if (nextSelectedAppt) {
      setSelectedAppt(nextSelectedAppt);
    }
  }, [appointmentsById, persistedState?.selectedApptId, selectedAppt]);

  const clearSelections = useCallback(() => {
    setSelectedTask(null);
    setSelectedAppt(null);
    setTaskFromReadiness(null);
    dispatch(
      saveCareOrchestrationState({
        selectedTaskId: null,
        selectedApptId: null,
        taskFromReadinessId: null,
      }),
    );
  }, [dispatch]);

  const handleSetPatientSearch = useCallback(
    (nextPatientSearch) => {
      setPatientSearch(nextPatientSearch);
      dispatch(saveCareOrchestrationState({ patientSearch: nextPatientSearch }));
    },
    [dispatch],
  );

  const expandPatientGroup = useCallback(
    (patientGroupKey) => {
      if (!patientGroupKey || expandedPatients[patientGroupKey]) return;

      const nextExpandedPatients = {
        ...expandedPatients,
        [patientGroupKey]: true,
      };

      setExpandedPatients(nextExpandedPatients);
      dispatch(saveCareOrchestrationState({ expandedPatients: nextExpandedPatients }));
    },
    [dispatch, expandedPatients],
  );

  useEffect(() => {
    if (!hasEnabledTabs) {
      if (
        selectedTask ||
        selectedAppt ||
        taskFromReadiness ||
        persistedState?.selectedTaskId ||
        persistedState?.selectedApptId ||
        persistedState?.taskFromReadinessId
      ) {
        clearSelections();
      }
      if (location.pathname !== CARE_ORCHESTRATION_BASE_PATH) {
        navigate(CARE_ORCHESTRATION_BASE_PATH, { replace: true });
      }
      return;
    }

    const canonicalPath = getCareOrchestrationPath(
      view,
      view === TAB_KEYS.TASKS && (requestedRoute.statusFilter || requestedRoute.taskId)
        ? requestedRoute
        : null,
    );
    if (location.pathname !== canonicalPath) {
      if (requestedView && requestedView !== view) {
        clearSelections();
      }
      navigate(canonicalPath, { replace: true });
    }
  }, [
    clearSelections,
    hasEnabledTabs,
    location.pathname,
    navigate,
    persistedState?.selectedApptId,
    persistedState?.selectedTaskId,
    persistedState?.taskFromReadinessId,
    requestedView,
    selectedAppt,
    selectedTask,
    taskFromReadiness,
    view,
  ]);

  useEffect(() => {
    if (view !== TAB_KEYS.TASKS || (!requestedRoute.statusFilter && !requestedRoute.taskId)) return;

    const routeKey = `${location.key}:${location.pathname}`;
    if (appliedTaskRouteKeyRef.current === routeKey) return;

    if (ownerFilter !== 'all') {
      setOwnerFilter('all');
      dispatch(saveCareOrchestrationState({ ownerFilter: 'all' }));
    }

    if (activeTeam !== 'all') {
      setActiveTeam('all');
      dispatch(saveCareOrchestrationState({ activeTeam: 'all' }));
    }

    const nextStatusFilter = requestedRoute.statusFilter ?? 'open';
    if (statusFilter !== nextStatusFilter) {
      setStatusFilter(nextStatusFilter);
      dispatch(saveCareOrchestrationState({ statusFilter: nextStatusFilter }));
    }

    if (requestedRoute.patientId) {
      expandPatientGroup(`patient-${requestedRoute.patientId}`);
    }

    if (!requestedRoute.taskId) {
      appliedTaskRouteKeyRef.current = routeKey;
      return;
    }

    if (isCareOrchestrationLoading) return;

    const requestedTask = tasksById[requestedRoute.taskId];
    const taskMatchesRoute =
      !!requestedTask &&
      (!requestedRoute.patientId ||
        `${getTaskPatientId(requestedTask) ?? ''}` === requestedRoute.patientId) &&
      (!requestedRoute.statusFilter ||
        matchesTaskStatusFilter(requestedTask, requestedRoute.statusFilter));

    if (taskMatchesRoute) {
      const requestedTaskPatient = {
        id: requestedTask.patientId,
        firstName: requestedTask.patient.firstName,
        lastName: requestedTask.patient.lastName,
        preferredName: requestedTask.patient.preferredName,
        dob: requestedTask.patient.dob,
        diabetesStatus: requestedTask.patient.diabetesStatus,
        cognitiveStatus: requestedTask.patient.cognitiveStatus,
      };

      if (`${patientSearch?.id ?? ''}` !== `${requestedTask.patientId}`) {
        handleSetPatientSearch(requestedTaskPatient);
      }

      expandPatientGroup(getTaskPatientGroupKey(requestedTask));

      if (selectedTask?.id !== requestedTask.id) {
        setSelectedTask(requestedTask);
        dispatch(saveCareOrchestrationState({ selectedTaskId: requestedTask.id }));
      }
      appliedTaskRouteKeyRef.current = routeKey;
      return;
    }

    if (!requestedTask) {
      return;
    }

    if (selectedTask || persistedState?.selectedTaskId) {
      setSelectedTask(null);
      dispatch(saveCareOrchestrationState({ selectedTaskId: null }));
    }

    appliedTaskRouteKeyRef.current = routeKey;
  }, [
    activeTeam,
    dispatch,
    expandPatientGroup,
    isCareOrchestrationLoading,
    location.key,
    location.pathname,
    ownerFilter,
    patientSearch?.id,
    persistedState?.selectedTaskId,
    requestedRoute.patientId,
    requestedRoute.statusFilter,
    requestedRoute.taskId,
    selectedTask,
    statusFilter,
    tasksById,
    view,
  ]);

  useEffect(() => {
    if (!view) return;
    dispatch(saveCareOrchestrationView(view));
  }, [dispatch, view]);

  const handleChangeView = (nextView) => {
    if (!nextView || nextView === view) return;
    navigate(getCareOrchestrationPath(nextView));
  };

  const handleResetTaskRoute = () => {
    if (view !== TAB_KEYS.TASKS) return;

    const baseTasksPath = getCareOrchestrationPath(TAB_KEYS.TASKS);
    if (location.pathname === baseTasksPath) return;

    navigate(baseTasksPath, { replace: true });
  };

  const handleSetSelectedTask = (task) => {
    handleResetTaskRoute();
    setSelectedTask(task);
    dispatch(saveCareOrchestrationState({ selectedTaskId: task?.id || null }));
  };

  const handleSetOwnerFilter = (nextOwnerFilter) => {
    handleResetTaskRoute();
    setOwnerFilter(nextOwnerFilter);
    dispatch(saveCareOrchestrationState({ ownerFilter: nextOwnerFilter }));
  };

  const handleSetActiveTeam = (nextActiveTeam) => {
    handleResetTaskRoute();
    setActiveTeam(nextActiveTeam);
    dispatch(saveCareOrchestrationState({ activeTeam: nextActiveTeam }));
  };

  const handleSetStatusFilter = (nextStatusFilter) => {
    handleResetTaskRoute();
    setStatusFilter(nextStatusFilter);
    dispatch(saveCareOrchestrationState({ statusFilter: nextStatusFilter }));
  };

  const handleSort = (nextKey) => {
    handleResetTaskRoute();
    if (nextKey === sortKey) {
      const nextSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(nextSortDirection);
      dispatch(saveCareOrchestrationState({ sortDirection: nextSortDirection }));
      return;
    }

    setSortKey(nextKey);
    setSortDirection('asc');
    dispatch(
      saveCareOrchestrationState({
        sortKey: nextKey,
        sortDirection: 'asc',
      }),
    );
  };

  const handleSetSelectedAppt = (appointment) => {
    setSelectedAppt(appointment);
    dispatch(saveCareOrchestrationState({ selectedApptId: appointment?.id || null }));
  };

  const handleSetTaskFromReadiness = (task) => {
    setTaskFromReadiness(task);
    dispatch(saveCareOrchestrationState({ taskFromReadinessId: task?.id || null }));
  };

  const handleSetExpandedPatients = (nextExpandedPatients) => {
    handleResetTaskRoute();
    const resolvedExpandedPatients =
      typeof nextExpandedPatients === 'function'
        ? nextExpandedPatients(expandedPatients)
        : nextExpandedPatients;

    setExpandedPatients(resolvedExpandedPatients);
    dispatch(saveCareOrchestrationState({ expandedPatients: resolvedExpandedPatients }));
  };

  const readinessStats = useMemo(
    () =>
      sortedAppts.reduce(
        (acc, appointment) => {
          const readinessStatus = getAppointmentReadinessStatus(
            appointmentTasksById[appointment.id] || [],
          );

          if (readinessStatus === 'green') acc.green += 1;
          if (readinessStatus === 'yellow') acc.yellow += 1;
          if (readinessStatus === 'red') acc.red += 1;

          return acc;
        },
        { red: 0, yellow: 0, green: 0 },
      ),
    [appointmentTasksById, sortedAppts],
  );

  const patientScopedTasks = useMemo(() => {
    if (!patientSearch?.id) return availableTasks;

    return availableTasks.filter(
      (task) => `${getTaskPatientId(task) ?? ''}` === `${patientSearch.id}`,
    );
  }, [availableTasks, patientSearch?.id]);

  const filtered = useMemo(
    () =>
      patientScopedTasks
        .filter((task) => {
          const ownerLabel = `${task?.owner || ''}`.trim().toLowerCase();
          const isUnassigned = !ownerLabel || ownerLabel === 'unassigned';
          const isMyTask = !!currentUserNameLower && ownerLabel === currentUserNameLower;
          const isSessionCompletedTask = !!sessionCompletedTaskIds[task.id];

          if (ownerFilter === 'my' && !isMyTask) return false;
          if (ownerFilter === 'unassigned' && !isUnassigned) return false;
          if (activeTeam !== 'all' && task.team !== activeTeam) return false;
          if (!isSessionCompletedTask && !matchesTaskStatusFilter(task, statusFilter)) return false;
          return true;
        })
        .sort((a, b) => {
          const priority = {
            [TASK_PRIORITY.Critical]: 0,
            [TASK_PRIORITY.High]: 1,
            [TASK_PRIORITY.Medium]: 2,
            [TASK_PRIORITY.Low]: 3,
          };
          const statusOrder = {
            pending: 0,
            'in-progress': 1,
            [TASK_DISPLAY_STATUS.Done]: 2,
          };

          const dir = sortDirection === 'asc' ? 1 : -1;
          let primary = 0;

          if (sortKey === 'status') {
            primary =
              (statusOrder[getTaskDisplayStatus(a)] ?? 99) -
              (statusOrder[getTaskDisplayStatus(b)] ?? 99);
          } else if (sortKey === 'priority') {
            primary = (priority[a.priority] ?? 99) - (priority[b.priority] ?? 99);
          } else {
            const aDue = a.due ? new Date(a.due).getTime() : Number.POSITIVE_INFINITY;
            const bDue = b.due ? new Date(b.due).getTime() : Number.POSITIVE_INFINITY;
            primary = aDue - bDue;
          }

          if (primary !== 0) return dir * primary;

          const aDue = a.due ? new Date(a.due).getTime() : Number.POSITIVE_INFINITY;
          const bDue = b.due ? new Date(b.due).getTime() : Number.POSITIVE_INFINITY;
          return aDue - bDue || priority[a.priority] - priority[b.priority];
        }),
    [
      activeTeam,
      currentUserNameLower,
      ownerFilter,
      patientScopedTasks,
      sessionCompletedTaskIds,
      sortDirection,
      sortKey,
      statusFilter,
    ],
  );

  useEffect(() => {
    if (!selectedTask) return;

    const hasSelectedTask = filtered.some((task) => task.id === selectedTask.id);
    if (hasSelectedTask) return;

    setSelectedTask(null);
    dispatch(saveCareOrchestrationState({ selectedTaskId: null }));
  }, [dispatch, filtered, selectedTask]);

  const ownerStats = useMemo(() => {
    if (!patientScopedTasks.length) return DEFAULT_OWNER_STATS;

    const baseTasks = patientScopedTasks.filter((task) => {
      if (activeTeam !== 'all' && task.team !== activeTeam) return false;
      return matchesTaskStatusFilter(task, statusFilter);
    });

    const my = baseTasks.filter((task) => {
      const ownerLabel = `${task?.owner || ''}`.trim().toLowerCase();
      return !!currentUserNameLower && ownerLabel === currentUserNameLower;
    }).length;

    const unassigned = baseTasks.filter((task) => {
      const ownerLabel = `${task?.owner || ''}`.trim().toLowerCase();
      return !ownerLabel || ownerLabel === 'unassigned';
    }).length;

    return {
      all: baseTasks.length,
      my,
      unassigned,
    };
  }, [activeTeam, currentUserNameLower, patientScopedTasks, statusFilter]);

  const stats = useMemo(() => {
    if (patientScopedTasks.length) {
      return {
        total: patientScopedTasks.filter(
          (task) => getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        member: patientScopedTasks.filter(
          (task) =>
            task.team === 'member' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        ops: patientScopedTasks.filter(
          (task) => task.team === 'ops' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        clinical: patientScopedTasks.filter(
          (task) =>
            task.team === 'clinical' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        rcm: patientScopedTasks.filter(
          (task) => task.team === 'rcm' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        cognitive: patientScopedTasks.filter(
          (task) =>
            task.pathway === 'cognitive' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        kisunla: patientScopedTasks.filter(
          (task) =>
            task.pathway === 'kisunla' && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        'leqembi-iv': patientScopedTasks.filter(
          (task) =>
            task.pathway === 'leqembi-iv' &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        'leqembi-sc': patientScopedTasks.filter(
          (task) =>
            task.pathway === 'leqembi-sc' &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        critical: patientScopedTasks.filter(
          (task) =>
            task.priority === TASK_PRIORITY.Critical &&
            getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        blocked: patientScopedTasks.filter((task) => isTaskBlockedStatus(task)).length,
        escalated: patientScopedTasks.filter((task) => isTaskEscalatedStatus(task)).length,
        messages: patientScopedTasks.filter(
          (task) => isMessageTask(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
        assessments: patientScopedTasks.filter(
          (task) =>
            isAssessmentTask(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done,
        ).length,
      };
    }

    return DEFAULT_STATS;
  }, [patientScopedTasks]);

  return (
    <div className={styles.root}>
      <CareOrchestrationHeader
        view={view}
        readinessStats={readinessStats}
        stats={stats}
        tabAvailability={{
          [TAB_KEYS.READINESS]: isReadinessEnabled,
          [TAB_KEYS.TASKS]: isTasksEnabled,
        }}
        onChangeView={(nextView) => {
          const isNextViewEnabled = availableTabs.some(
            (tab) => tab.key === nextView && tab.enabled,
          );
          if (!isNextViewEnabled) return;
          handleChangeView(nextView);
        }}
        onClearSelections={clearSelections}
      />

      <div className={styles.content}>
        <div className={styles.contentInner}>
          {!hasEnabledTabs && (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateTitle}>Care Orchestration is coming soon</div>
              <div className={styles.emptyStateText}>
                Tasks and Readiness will appear here once those feature flags are enabled.
              </div>
            </div>
          )}

          {hasEnabledTabs && view === TAB_KEYS.READINESS && (
            <ReadinessView
              isLoading={isCareOrchestrationLoading}
              apptsByDate={apptsByDate}
              readinessStats={readinessStats}
              selectedAppt={selectedAppt}
              taskFromReadiness={taskFromReadiness}
              appointmentTasksById={appointmentTasksById}
              setSelectedAppt={handleSetSelectedAppt}
              setTaskFromReadiness={handleSetTaskFromReadiness}
              onMarkComplete={handleMarkComplete}
              markingTaskId={markingTaskId}
              onReassignTask={handleReassignTask}
              reassigningTaskId={reassigningTaskId}
              onAssignToMe={handleAssignToMe}
              assigningTaskId={assigningTaskId}
              onUnassignTask={handleUnassignTask}
              unassigningTaskId={unassigningTaskId}
              onBlockTask={handleBlockTask}
              blockingTaskId={blockingTaskId}
              onUnblockTask={handleUnblockTask}
              unblockingTaskId={unblockingTaskId}
              onEscalateTask={handleEscalateTask}
              escalatingTaskId={escalatingTaskId}
              onDeescalateTask={handleDeescalateTask}
              deescalatingTaskId={deescalatingTaskId}
              currentUserName={currentUserName}
              onReplySent={refreshCareOrchestrationData}
            />
          )}

          {hasEnabledTabs && view === TAB_KEYS.TASKS && (
            <TasksView
              ownerFilter={ownerFilter}
              setOwnerFilter={handleSetOwnerFilter}
              ownerStats={ownerStats}
              currentUserNameLower={currentUserNameLower}
              activeTeam={activeTeam}
              setActiveTeam={handleSetActiveTeam}
              statusFilter={statusFilter}
              setStatusFilter={handleSetStatusFilter}
              patientSearch={patientSearch}
              setPatientSearch={handleSetPatientSearch}
              stats={stats}
              filtered={filtered}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort}
              selectedTask={selectedTask}
              setSelectedTask={handleSetSelectedTask}
              expandedPatients={expandedPatients}
              setExpandedPatients={handleSetExpandedPatients}
              onAssignToMe={handleAssignToMe}
              assigningTaskId={assigningTaskId}
              onUnassignTask={handleUnassignTask}
              unassigningTaskId={unassigningTaskId}
              onMarkComplete={handleMarkComplete}
              markingTaskId={markingTaskId}
              onReassignTask={handleReassignTask}
              reassigningTaskId={reassigningTaskId}
              onBlockTask={handleBlockTask}
              blockingTaskId={blockingTaskId}
              onUnblockTask={handleUnblockTask}
              unblockingTaskId={unblockingTaskId}
              onEscalateTask={handleEscalateTask}
              escalatingTaskId={escalatingTaskId}
              onDeescalateTask={handleDeescalateTask}
              deescalatingTaskId={deescalatingTaskId}
              currentUserName={currentUserName}
              onReplySent={refreshCareOrchestrationData}
            />
          )}
        </div>
      </div>
    </div>
  );
}
